/**
 * Editor Top Bar — Premium Visual Editor Chrome
 *
 * World-class toolbar: clean, spacious, with intentional visual hierarchy.
 * Dropdown page selector supports 11 page types (grouped by category).
 * Cart, checkout, and auth pages use hardcoded designs with theme colors only.
 */

import React, { useState, useEffect } from 'react';
import {
  Save, Undo2, Redo2, Monitor, Smartphone, Tablet,
  Eye, Pencil, Palette, ArrowLeft, Loader2, ExternalLink,
  Building, ChevronDown, Layers, Clock, Check,
  Plus, Minus, Maximize2, Sparkles
} from 'lucide-react';

const PAGE_OPTIONS = [
  { value: 'main', label: 'Home Page', group: 'Main Pages' },
  { value: 'shop', label: 'Shop Page', group: 'Main Pages' },
  { value: 'product', label: 'Product Page', group: 'Main Pages' },
  { value: 'about', label: 'About Page', group: 'Main Pages' },
  { value: 'contact', label: 'Contact Page', group: 'Main Pages' },
  { value: 'navbar', label: 'Navigation Bar', group: 'Site-wide' },
  { value: 'footer', label: 'Footer', group: 'Site-wide' },
  { value: 'privacy', label: 'Privacy Policy', group: 'Info Pages' },
  { value: 'shipping', label: 'Shipping & Returns', group: 'Info Pages' },
  { value: 'terms', label: 'Terms & Conditions', group: 'Info Pages' },
  { value: 'faqs', label: 'FAQs', group: 'Info Pages' },
];

const EditorTopBar = ({
  templateName,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onSave,
  onSaveDraft,
  isSaving,
  isDraftSaving,
  isDirty,
  editMode,
  onToggleEditMode,
  previewDevice,
  onSetDevice,
  showThemePanel,
  onToggleThemePanel,
  showBusinessPanel,
  onToggleBusinessPanel,
  showSectionLibrary,
  onToggleSectionLibrary,
  showVersionHistory,
  onToggleVersionHistory,
  showAnimationPanel,
  onToggleAnimationPanel,
  onBack,
  onPreviewLive,
  currentPage,
  onPageChange,
  autoSaveStatus,
  zoomLevel,
  onZoomChange
}) => {
  const [saveFlash, setSaveFlash] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  // Flash effect after save completes
  useEffect(() => {
    if (!isSaving && justSaved) {
      setSaveFlash(true);
      const t = setTimeout(() => setSaveFlash(false), 1200);
      return () => clearTimeout(t);
    }
  }, [isSaving, justSaved]);

  const handleSave = () => {
    setJustSaved(true);
    onSave();
  };

  // Reset justSaved when dirty again
  useEffect(() => {
    if (isDirty) setJustSaved(false);
  }, [isDirty]);

  return (
    <div style={{
      height: '52px',
      background: '#ffffff',
      borderBottom: '1px solid #e8eaed',
      display: 'flex',
      alignItems: 'center',
      padding: '0 14px',
      gap: '8px',
      flexShrink: 0,
      boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
      position: 'relative',
      zIndex: 50,
    }}>

      {/* ── Left: Back + Template name + Page selector ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, flexShrink: 0, position: 'relative', zIndex: 2 }}>
        <button
          onClick={onBack}
          style={{
            width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fafafa', cursor: 'pointer',
            color: '#6b7280', transition: 'all 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.borderColor = '#d1d5db'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#fafafa'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
          title="Back to templates"
        >
          <ArrowLeft style={{ width: '16px', height: '16px' }} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <span style={{
            fontSize: '14px', fontWeight: 600, color: '#111827',
            maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            letterSpacing: '-0.01em',
          }}>
            {templateName || 'Template Editor'}
          </span>
          {isDirty && (
            <span style={{
              width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
              boxShadow: '0 0 6px rgba(245,158,11,0.5)',
              animation: 'pulse 2s ease-in-out infinite',
            }} title="Unsaved changes" />
          )}
          {saveFlash && !isDirty && (
            <span style={{
              display: 'flex', alignItems: 'center', gap: '3px',
              fontSize: '11px', fontWeight: 600, color: '#059669',
              animation: 'fadeInUp 0.3s ease-out',
            }}>
              <Check style={{ width: '12px', height: '12px' }} />
              Saved
            </span>
          )}
        </div>

        {/* Page selector — dropdown */}
        {onPageChange && (
          <>
            <div style={{ width: '1px', height: '24px', background: '#e5e7eb' }} />
            <div style={{ position: 'relative' }}>
              <select
                value={currentPage || 'main'}
                onChange={(e) => onPageChange(e.target.value)}
                style={{
                  padding: '5px 28px 5px 10px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  background: '#f9fafb',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#111827',
                  cursor: 'pointer',
                  outline: 'none',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 8px center',
                }}
              >
                {(() => {
                  const groups = {};
                  PAGE_OPTIONS.forEach(opt => {
                    if (!groups[opt.group]) groups[opt.group] = [];
                    groups[opt.group].push(opt);
                  });
                  return Object.entries(groups).map(([group, options]) => (
                    <optgroup key={group} label={group}>
                      {options.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </optgroup>
                  ));
                })()}
              </select>
            </div>
          </>
        )}
      </div>

      {/* ── Center: Undo/Redo + Device + Edit/Preview ── */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>

        {/* Undo / Redo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
          <ToolbarIconButton
            icon={Undo2}
            onClick={onUndo}
            disabled={!canUndo}
            title="Undo (Ctrl+Z)"
          />
          <ToolbarIconButton
            icon={Redo2}
            onClick={onRedo}
            disabled={!canRedo}
            title="Redo (Ctrl+Y)"
          />
        </div>

        <div style={{ width: '1px', height: '24px', background: '#e5e7eb', margin: '0 6px' }} />

        {/* Device toggle */}
        <div style={{
          display: 'flex', alignItems: 'center', background: '#f3f4f6', borderRadius: '10px', padding: '3px',
        }}>
          {[
            { device: 'desktop', Icon: Monitor, label: 'Desktop' },
            { device: 'tablet', Icon: Tablet, label: 'Tablet' },
            { device: 'mobile', Icon: Smartphone, label: 'Mobile' },
          ].map(({ device, Icon, label }) => {
            const isActive = previewDevice === device;
            return (
              <button
                key={device}
                onClick={() => onSetDevice(device)}
                style={{
                  width: '34px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '7px', border: 'none', cursor: 'pointer',
                  background: isActive ? '#ffffff' : 'transparent',
                  color: isActive ? '#3b82f6' : '#9ca3af',
                  boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s ease',
                }}
                title={`${label} view`}
              >
                <Icon style={{ width: '16px', height: '16px' }} />
              </button>
            );
          })}
        </div>

        {/* Zoom controls */}
        {onZoomChange && (
          <>
            <div style={{ width: '1px', height: '24px', background: '#e5e7eb', margin: '0 4px' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
              <ToolbarIconButton
                icon={Minus}
                onClick={() => onZoomChange(Math.max(25, (zoomLevel || 100) - 25))}
                disabled={(zoomLevel || 100) <= 25}
                title="Zoom out (Ctrl+−)"
              />
              <button
                onClick={() => onZoomChange(100)}
                style={{
                  padding: '3px 6px', borderRadius: '6px', border: 'none',
                  background: (zoomLevel || 100) !== 100 ? '#f0f9ff' : 'transparent',
                  color: (zoomLevel || 100) !== 100 ? '#3b82f6' : '#6b7280',
                  fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                  minWidth: '40px', textAlign: 'center',
                  transition: 'all 0.15s',
                }}
                title="Reset zoom (Ctrl+0)"
              >
                {zoomLevel || 100}%
              </button>
              <ToolbarIconButton
                icon={Plus}
                onClick={() => onZoomChange(Math.min(200, (zoomLevel || 100) + 25))}
                disabled={(zoomLevel || 100) >= 200}
                title="Zoom in (Ctrl+=)"
              />
            </div>
          </>
        )}

        <div style={{ width: '1px', height: '24px', background: '#e5e7eb', margin: '0 4px' }} />

        {/* Edit / Preview toggle */}
        <div style={{
          display: 'flex', alignItems: 'center', background: '#f3f4f6', borderRadius: '10px', padding: '3px',
        }}>
          <button
            onClick={() => { if (!editMode) onToggleEditMode(); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '5px',
              padding: '5px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer',
              fontSize: '12px', fontWeight: editMode ? 600 : 500,
              background: editMode ? '#ffffff' : 'transparent',
              color: editMode ? '#111827' : '#9ca3af',
              boxShadow: editMode ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.2s ease',
            }}
          >
            <Pencil style={{ width: '12px', height: '12px' }} />
            Edit
          </button>
          <button
            onClick={() => { if (editMode) onToggleEditMode(); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '5px',
              padding: '5px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer',
              fontSize: '12px', fontWeight: !editMode ? 600 : 500,
              background: !editMode ? '#ffffff' : 'transparent',
              color: !editMode ? '#111827' : '#9ca3af',
              boxShadow: !editMode ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.2s ease',
            }}
          >
            <Eye style={{ width: '12px', height: '12px' }} />
            Preview
          </button>
        </div>
      </div>

      {/* ── Right: Panels + Preview + Save ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>

        <PanelToggleButton
          icon={Building}
          label="Brand Info"
          isActive={showBusinessPanel}
          onClick={onToggleBusinessPanel}
          activeColor="#3b82f6"
          activeBg="#eff6ff"
        />

        <PanelToggleButton
          icon={Palette}
          label="Theme"
          isActive={showThemePanel}
          onClick={onToggleThemePanel}
          activeColor="#8b5cf6"
          activeBg="#f5f3ff"
        />

        {onToggleSectionLibrary && (
          <PanelToggleButton
            icon={Layers}
            label="Sections"
            isActive={showSectionLibrary}
            onClick={onToggleSectionLibrary}
            activeColor="#6366f1"
            activeBg="#eef2ff"
          />
        )}

        {onToggleAnimationPanel && (
          <PanelToggleButton
            icon={Sparkles}
            label="Animate"
            isActive={showAnimationPanel}
            onClick={onToggleAnimationPanel}
            activeColor="#ec4899"
            activeBg="#fdf2f8"
          />
        )}

        {onToggleVersionHistory && (
          <PanelToggleButton
            icon={Clock}
            label="History"
            isActive={showVersionHistory}
            onClick={onToggleVersionHistory}
            activeColor="#d97706"
            activeBg="#fffbeb"
          />
        )}

        {onPreviewLive && (
          <ToolbarIconButton
            icon={ExternalLink}
            onClick={onPreviewLive}
            title="Preview live site"
          />
        )}

        <div style={{ width: '1px', height: '24px', background: '#e5e7eb', margin: '0 4px' }} />

        {/* Auto-save status indicator */}
        {autoSaveStatus === 'saving' && (
          <span style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            fontSize: '12px', color: '#9ca3af', fontWeight: 500,
            marginRight: '4px', animation: 'fadeInUp 0.3s ease-out',
          }}>
            <Loader2 style={{ width: '12px', height: '12px', animation: 'spin 0.8s linear infinite' }} />
            Auto-saving...
          </span>
        )}
        {autoSaveStatus === 'saved' && (
          <span style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            fontSize: '12px', color: '#9ca3af', fontWeight: 500,
            marginRight: '4px', animation: 'fadeInUp 0.3s ease-out',
          }}>
            <Check style={{ width: '12px', height: '12px', color: '#059669' }} />
            All changes saved
          </span>
        )}

        {/* Draft button */}
        {onSaveDraft && isDirty && (
          <button
            onClick={onSaveDraft}
            disabled={isDraftSaving}
            style={{
              display: 'flex', alignItems: 'center', gap: '5px',
              padding: '6px 14px', borderRadius: '8px',
              border: '1px solid #e5e7eb',
              cursor: isDraftSaving ? 'default' : 'pointer',
              fontSize: '12px', fontWeight: 500,
              background: '#ffffff',
              color: isDraftSaving ? '#9ca3af' : '#6b7280',
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => { if (!isDraftSaving) { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#374151'; }}}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#6b7280'; }}
          >
            {isDraftSaving ? (
              <Loader2 style={{ width: '12px', height: '12px', animation: 'spin 0.8s linear infinite' }} />
            ) : (
              <Save style={{ width: '12px', height: '12px' }} />
            )}
            {isDraftSaving ? 'Saving...' : 'Save Draft'}
          </button>
        )}

        {/* Publish button — premium CTA */}
        <button
          onClick={handleSave}
          disabled={isSaving || !isDirty}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '7px 20px', borderRadius: '10px', border: 'none',
            cursor: isSaving || !isDirty ? 'default' : 'pointer',
            fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.02em',
            background: isSaving
              ? '#2563eb'
              : isDirty
                ? '#111827'
                : '#f3f4f6',
            color: isSaving || isDirty ? '#ffffff' : '#b0b5bd',
            boxShadow: isDirty && !isSaving
              ? '0 2px 8px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)'
              : 'none',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: isSaving ? 'scale(0.97)' : 'scale(1)',
          }}
          onMouseEnter={e => {
            if (isDirty && !isSaving) {
              e.currentTarget.style.background = '#1e293b';
              e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.08)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={e => {
            if (isDirty && !isSaving) {
              e.currentTarget.style.background = '#111827';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)';
              e.currentTarget.style.transform = 'scale(1)';
            }
          }}
        >
          {isSaving ? (
            <Loader2 style={{ width: '13px', height: '13px', animation: 'spin 0.8s linear infinite' }} />
          ) : saveFlash && !isDirty ? (
            <Check style={{ width: '13px', height: '13px' }} />
          ) : (
            <Save style={{ width: '13px', height: '13px' }} />
          )}
          {isSaving ? 'Saving...' : saveFlash && !isDirty ? 'Saved!' : isDirty ? 'Save & Go Live' : 'All Saved'}
        </button>
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

/* ── Reusable toolbar icon button ── */
function ToolbarIconButton({ icon: Icon, onClick, disabled, title }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      onMouseEnter={() => !disabled && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: '8px', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
        background: hovered && !disabled ? '#f3f4f6' : 'transparent',
        color: disabled ? '#d1d5db' : (hovered ? '#111827' : '#6b7280'),
        transition: 'all 0.15s ease',
      }}
    >
      <Icon style={{ width: '16px', height: '16px' }} />
    </button>
  );
}

/* ── Panel toggle button with label ── */
function PanelToggleButton({ icon: Icon, label, isActive, onClick, activeColor, activeBg }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: '5px',
        padding: '5px 10px', borderRadius: '8px', border: 'none', cursor: 'pointer',
        fontSize: '11px', fontWeight: isActive ? 600 : 500,
        background: isActive ? activeBg : (hovered ? '#f9fafb' : 'transparent'),
        color: isActive ? activeColor : '#6b7280',
        transition: 'all 0.2s ease',
      }}
      title={label}
    >
      <Icon style={{ width: '15px', height: '15px' }} />
      <span style={{ display: isActive ? 'inline' : undefined }}>{label}</span>
    </button>
  );
}

export default EditorTopBar;
